import type { FC} from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  alpha,
  AppBar,
  Box,
  Container,
  CssBaseline,
  Toolbar,
  useMediaQuery,
  useScrollTrigger,
  useTheme,
} from '@/shared/ui/mui';
import type { Audience } from '@/features/landing/unified/hooks/useAudienceToggle';
import { NavbarContent } from '../navigation';
import { useNavigationIdentity } from './hooks';
import { isShellRoute } from './utils';

const Navigation: FC = () => {
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isHomePage = location.pathname === '/' || location.pathname === '/home';
  const shellRoute = isShellRoute(location.pathname);

  // Audience toggle state for landing page
  const viewParam = searchParams.get('view');
  const audience: Audience = viewParam === 'providers' ? 'providers' : 'clients';
  const handleAudienceToggle = (newAudience: Audience) => {
    if (newAudience === 'providers') {
      setSearchParams({ view: 'providers' }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const identity = useNavigationIdentity();

  // Handle scroll events
  const scrolled = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });

  useEffect(() => {
    setIsScrolled(scrolled);
  }, [scrolled]);

  // Dashboard shell provides its own AppBar and SideMenu
  if (shellRoute) return null;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {isHomePage ? (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            top: 'var(--promo-offset, 0px)',
            width: '100%',
            zIndex: theme.zIndex.drawer + 1,
            transition: theme.transitions.create(
              ['width', 'margin', 'background-color', 'top'],
              {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              },
            ),
            background: isScrolled ? 'white !important' : `${theme.palette.black.main} !important`,
            backgroundColor: isScrolled
              ? 'white !important'
              : `${theme.palette.black.main} !important`,
            backdropFilter: isScrolled ? 'blur(8px)' : 'none',
            boxShadow: isScrolled
              ? `0px 2px 4px ${alpha(theme.palette.common.black, 0.1)}`
              : 'none',
          }}
        >
          <Container
            maxWidth="xl"
            sx={{
              backgroundColor: 'transparent',
              background: 'transparent',
            }}
          >
            <Toolbar
              disableGutters
              sx={{
                backgroundColor: 'transparent',
                background: 'transparent',
              }}
            >
              <NavbarContent
                identity={identity}
                isLargeScreen={!isMobile}
                isHomePage={true}
                isScrolled={scrolled}
                audience={audience}
                onAudienceToggle={handleAudienceToggle}
              />
            </Toolbar>
          </Container>
        </AppBar>
      ) : (
        <AppBar
          position="fixed"
          sx={{
            top: 'var(--promo-offset, 0px)',
            width: '100%',
            zIndex: theme.zIndex.drawer + 1,
            transition: theme.transitions.create(['width', 'margin', 'top'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            backgroundColor: 'white',
            boxShadow: `0px 2px 4px ${alpha(theme.palette.common.black, 0.1)}`,
          }}
        >
          <Container
            maxWidth="xl"
            sx={{
              backgroundColor: 'transparent',
            }}
          >
            <Toolbar
              disableGutters
              sx={{
                backgroundColor: 'transparent',
              }}
            >
              <NavbarContent
                identity={identity}
                isLargeScreen={!isMobile}
                isHomePage={false}
                isScrolled={true}
              />
            </Toolbar>
          </Container>
        </AppBar>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          pt: 0,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Toolbar />
        <Box sx={{ height: 'var(--promo-offset, 0px)', transition: 'height 0.3s ease' }} />
      </Box>
    </Box>
  );
};

export default Navigation;
